import React, { useMemo } from 'react'
import { Pie } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Spin } from 'antd'
import { Table } from '@surecompanies/core_components'
import { Stack, Typography } from '@sureco/design-system'
import { useOperatorPerformance } from '../hooks/useOperatorPerformance'
import { TIMEZONE_PST } from 'helpers/datetime'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(timezone)

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend)

interface SummaryRow {
  key: string
  label: string
  value: number
}

interface SummaryTableProps {
  selectedBenefitEffectiveDate: string
}

const PIE_CHART_COLORS = {
  totalSureCoSubmitted: '#1890ff',
  totalValidated: '#52c41a',
  totalUnsubmitted: '#faad14',
} as const

export const Summary: React.FC<SummaryTableProps> = ({
  selectedBenefitEffectiveDate,
}) => {
  const { data, isLoading, error } = useOperatorPerformance({
    effectiveDate: selectedBenefitEffectiveDate,
    timezone: TIMEZONE_PST,
  })

  const { pieChartData, tableData } = useMemo(() => {
    if (!data?.summary) {
      return {
        pieChartData: null,
        tableData: [],
      }
    }

    const {
      totalWorkerSubmitted,
      totalSureCoSubmitted,
      todaySubmitted,
      totalValidated,
      totalUnsubmitted,
    } = data.summary

    const rows: SummaryRow[] = [
      {
        key: 'totalWorkerSubmitted',
        label: 'Total Worker Submitted',
        value: totalWorkerSubmitted,
      },
      {
        key: 'totalSureCoSubmitted',
        label: 'SureCo Submitted',
        value: totalSureCoSubmitted,
      },
      {
        key: 'totalValidated',
        label: 'Validated',
        value: totalValidated,
      },
      {
        key: 'todaySubmitted',
        label: 'Today Submitted',
        value: todaySubmitted,
      },
      {
        key: 'totalUnsubmitted',
        label: 'Not yet submitted',
        value: totalUnsubmitted,
      },
    ]

    return {
      pieChartData: {
        labels: ['SureCo Submitted', 'Validated', 'Not yet submitted'],
        datasets: [
          {
            data: [totalSureCoSubmitted, totalValidated, totalUnsubmitted],
            backgroundColor: [
              PIE_CHART_COLORS.totalSureCoSubmitted,
              PIE_CHART_COLORS.totalValidated,
              PIE_CHART_COLORS.totalUnsubmitted,
            ],
            borderColor: [
              PIE_CHART_COLORS.totalSureCoSubmitted,
              PIE_CHART_COLORS.totalValidated,
              PIE_CHART_COLORS.totalUnsubmitted,
            ],
            borderWidth: 1,
          },
        ],
      },
      tableData: rows,
    }
  }, [data?.summary])

  const columns = [
    {
      title: 'Metric',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: number) => value?.toLocaleString() || '0',
      align: 'right' as const,
    },
  ]

  if (error) {
    return (
      <div>
        <Typography as="h2" size={20} bold>
          Summary
        </Typography>
        <div>Error loading summary data. Please try again later.</div>
      </div>
    )
  }

  return (
    <div>
      <Typography as="h2" size={20} bold>
        Summary
      </Typography>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <Stack
          direction="row"
          space={24}
          style={{ marginTop: '24px' }}
          align="center"
        >
          <div style={{ flex: '0 0 400px' }}>
            {pieChartData && (
              <Pie
                data={pieChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const label = context.label || ''
                          const value = context.parsed || 0
                          const total = context.dataset.data.reduce(
                            (a: number, b: number) => a + b,
                            0
                          )
                          const percentage =
                            total > 0 ? ((value / total) * 100).toFixed(2) : 0
                          return `${label}: ${value.toLocaleString()} (${percentage}%)`
                        },
                      },
                    },
                  },
                }}
              />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              rowKey="key"
              showHeader={false}
            />
          </div>
        </Stack>
      )}
    </div>
  )
}
